import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// Предохранитель: если экран падает с ошибкой — показываем понятный экран,
// а НЕ белую пустоту. Остальное приложение (навигация) продолжает жить.
// ═══════════════════════════════════════════════════════════════════════════

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  message?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error?.message || String(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // В консоль — чтобы можно было разобрать причину
    console.error('[ErrorBoundary] Экран упал:', error, info?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 text-center">
          <div className="text-5xl mb-4">🙈</div>
          <h2 className="font-display font-bold text-lg mb-2">Что-то пошло не так</h2>
          <p className="text-sm text-ink-muted mb-4 max-w-xs leading-relaxed">
            Этот экран не открылся. Остальное приложение работает — можно вернуться на главную или обновить.
          </p>
          {this.state.message && (
            <pre className="text-[10px] text-ink-dim bg-bg-elevated border border-line rounded-lg p-2 mb-6 max-w-[85vw] overflow-x-auto whitespace-pre-wrap text-left">
              {this.state.message}
            </pre>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-xl bg-emerald-glow text-bg font-bold text-sm active:scale-95 transition-transform"
            >
              Обновить
            </button>
            <button
              onClick={() => { window.location.href = '/'; }}
              className="px-5 py-2.5 rounded-xl bg-bg-elevated text-ink-muted text-sm active:scale-95 transition-transform"
            >
              На главную
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
